import React, { SetStateAction, Dispatch, useState } from 'react'
import { E911FormState } from './useAddDevice'
import AddressField from '@components/common/AddressField/AddressField.component'
import { getEnv } from '@utils/env'
import { APIProvider } from '@vis.gl/react-google-maps'
import { USStates } from '@constants/Common/personalInfo.constants'
import { Checkbox, InputField, Modal, Select } from '@enamelai/design-system'

export type E911AddressModalProps = {
  show: boolean
  onHide: () => void
  data: E911FormState
  setData: Dispatch<SetStateAction<E911FormState>>
  onSaved: () => void
}

const GOOGLE_MAPS_API_KEY = getEnv('REACT_APP_GOOGLE_MAPS_API_KEY')

const selectString = (value: string | string[]): string =>
  typeof value === 'string' ? value : (value[0] ?? '')

const E911AddressModal = ({
  show,
  onHide,
  data,
  setData,
  onSaved,
}: E911AddressModalProps): React.ReactElement => {
  const [step, setStep] = useState<number>(1)
  const [confirmChecked, setConfirmChecked] = useState<boolean>(false)
  const [validationError, setValidationError] = useState<
    Record<string, string>
  >({})

  const validateFields = (): boolean => {
    const errors: Record<string, string> = {}
    if (!data.customer_name) {
      errors.customer_name = 'Customer / location name is required'
    }
    if (!data.address) {
      errors.address = 'Street address is required'
    }
    if (!data.city) {
      errors.city = 'City/town is required'
    }
    if (!data.region) {
      errors.region = 'State is required'
    }
    if (!data.postal_code) {
      errors.postal_code = 'Zip is required'
    }

    setValidationError(errors)
    return Object.keys(errors).length === 0
  }

  const handleChange = (field: keyof E911FormState, value: string): void => {
    if (Object.keys(validationError).length > 0) {
      validateFields()
    }
    setData((f) => ({ ...f, [field]: value }) as E911FormState)
  }

  const handleHide = (): void => {
    setStep(1)
    setConfirmChecked(false)
    setValidationError({})
    setData({} as E911FormState)
    onHide()
  }

  return (
    <Modal
      open={show}
      onClose={handleHide}
      closeOnBackdrop={false}
      size="md"
      className="scp-add-device-modals"
      title={
        step === 1 ? 'Emergency (E911) address' : 'Confirm emergency address'
      }
      description={
        step === 1
          ? 'Only one E911 address can be linked to each device. The address is set for emergency-call routing.'
          : 'This address will be used for emergency (911) calls. Incorrect information may delay emergency response.'
      }
      secondaryLabel={step === 1 ? 'Cancel' : 'Previous'}
      primaryLabel={step === 1 ? 'Continue' : 'Confirm'}
      primaryDisabled={step === 2 && !confirmChecked}
      onSecondaryClick={() => {
        if (step === 1) {
          handleHide()
          return
        }
        setStep(1)
        setConfirmChecked(false)
      }}
      onPrimaryClick={() => {
        if (step === 1) {
          if (validateFields()) setStep(2)
          return
        }
        onSaved()
      }}
    >
      {step === 1 ? (
        <div className="scp-e911-fields">
          <InputField
            variant="text"
            fullWidth
            label="Customer / location name"
            clearable={false}
            value={data.customer_name}
            onChange={(e) => handleChange('customer_name', e.target.value)}
            helperText={validationError.customer_name}
            error={!!validationError.customer_name}
          />
          <APIProvider apiKey={GOOGLE_MAPS_API_KEY} libraries={['places']}>
            <div>
              <AddressField
                label="Address"
                value={data.address}
                onChange={(e) => handleChange('address', e)}
                countries={['US']}
                onSelect={(place, parsed) => {
                  handleChange('address', parsed.formattedAddress2 ?? '')
                  handleChange('city', parsed.city ?? '')
                  handleChange('region', parsed.state ?? '')
                  handleChange('postal_code', parsed.postalCode ?? '')
                }}
                hint={validationError.address}
                destructive={!!validationError.address}
              />{' '}
            </div>
          </APIProvider>
          <div className="scp-pm-grid2">
            <InputField
              variant="text"
              fullWidth
              label="City/town"
              clearable={false}
              value={data.city}
              onChange={(e) => handleChange('city', e.target.value)}
              helperText={validationError.city}
              error={!!validationError.city}
            />
            <Select
              label="State"
              value={data.region || undefined}
              error={!!validationError.region}
              helperText={validationError.region}
              options={USStates}
              onChange={(value) => handleChange('region', selectString(value))}
            />
            <InputField
              variant="text"
              fullWidth
              label="Zip"
              clearable={false}
              value={data.postal_code}
              onChange={(e) => handleChange('postal_code', e.target.value)}
              helperText={validationError.postal_code}
              error={!!validationError.postal_code}
            />
          </div>
        </div>
      ) : (
        <div>
          <div className="scp-confirmation">
            {data.address} <br />
            {data.city}, {data.region} {data.postal_code} <br /> <br />
            Assigned to: <br />
            {data.customer_name}
          </div>
          <Checkbox
            className="e911-confirm"
            id="e911-confirm"
            label="I confirm that I have inputted the correct address information."
            checked={confirmChecked}
            onChange={(e) => setConfirmChecked(e.target.checked)}
          />
        </div>
      )}
    </Modal>
  )
}

export default E911AddressModal
